/**
 * ManualFloorplans — Demo floor-plan cards in a side-by-side grid
 * (AptSync / BrandFloorplansEmbed look) without ApartmentSync, map, or pricing.
 * Placeholder images + specs + CTAs that link to Contact Us.
 */

import {
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  EditableToken,
  EditableSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

interface LightboxImage {
  image: string;
  alt: string;
}

const ctaDef: SlotGroupDefinition = {
  label: 'Card CTA',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/contact',
      suggestedFields: ['property_website_url'],
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'Contact Us',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

const plan1Def: SlotGroupDefinition = {
  label: 'Floor Plan 1',
  fields: {
    image: {
      label: 'Floor Plan Image',
      type: 'image',
      defaultField: 'floorplans[0].floorplan_image_url',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'floorplans[0].floorplan_name',
      defaultValue: 'Studio floor plan',
    },
    name: {
      label: 'Plan Name',
      type: 'text',
      defaultField: 'floorplans[0].floorplan_name',
      defaultValue: 'Studio A1',
    },
    beds: {
      label: 'Beds',
      type: 'text',
      defaultField: 'floorplans[0].floorplan_beds_min',
      defaultValue: '0',
    },
    baths: {
      label: 'Baths',
      type: 'text',
      defaultField: 'floorplans[0].floorplan_baths_min',
      defaultValue: '1',
    },
    sqft: {
      label: 'Sq Ft',
      type: 'text',
      defaultField: 'floorplans[0].floorplan_sqft_min',
      defaultValue: '525',
    },
  },
  sourceBinding: {
    collection: 'floorplans',
    defaultIndex: 0,
    fieldMappings: {
      image: 'floorplan_image_url',
      alt: 'floorplan_name',
      name: 'floorplan_name',
      beds: 'floorplan_beds_min',
      baths: 'floorplan_baths_min',
      sqft: 'floorplan_sqft_min',
    },
  },
  generator: {
    prompt: 'Pick a distinct floor plan layout for this demo card — prefer a studio or smallest layout when available.',
    context: 'Card 1 of 4 in the manual floor plans grid. Different from cards 2–4. Use the floor plan line drawing when present.',
  },
};

const plan2Def: SlotGroupDefinition = {
  label: 'Floor Plan 2',
  fields: {
    image: {
      label: 'Floor Plan Image',
      type: 'image',
      defaultField: 'floorplans[1].floorplan_image_url',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'floorplans[1].floorplan_name',
      defaultValue: 'One bedroom floor plan',
    },
    name: {
      label: 'Plan Name',
      type: 'text',
      defaultField: 'floorplans[1].floorplan_name',
      defaultValue: 'One Bedroom B1',
    },
    beds: {
      label: 'Beds',
      type: 'text',
      defaultField: 'floorplans[1].floorplan_beds_min',
      defaultValue: '1',
    },
    baths: {
      label: 'Baths',
      type: 'text',
      defaultField: 'floorplans[1].floorplan_baths_min',
      defaultValue: '1',
    },
    sqft: {
      label: 'Sq Ft',
      type: 'text',
      defaultField: 'floorplans[1].floorplan_sqft_min',
      defaultValue: '725',
    },
  },
  sourceBinding: {
    collection: 'floorplans',
    defaultIndex: 1,
    fieldMappings: {
      image: 'floorplan_image_url',
      alt: 'floorplan_name',
      name: 'floorplan_name',
      beds: 'floorplan_beds_min',
      baths: 'floorplan_baths_min',
      sqft: 'floorplan_sqft_min',
    },
  },
  generator: {
    prompt: 'Pick a distinct floor plan layout for this demo card — prefer a one-bedroom when available.',
    context: 'Card 2 of 4 in the manual floor plans grid. Different from cards 1, 3, and 4. Use the floor plan line drawing when present.',
  },
};

const plan3Def: SlotGroupDefinition = {
  label: 'Floor Plan 3',
  fields: {
    image: {
      label: 'Floor Plan Image',
      type: 'image',
      defaultField: 'floorplans[2].floorplan_image_url',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'floorplans[2].floorplan_name',
      defaultValue: 'Two bedroom floor plan',
    },
    name: {
      label: 'Plan Name',
      type: 'text',
      defaultField: 'floorplans[2].floorplan_name',
      defaultValue: 'Two Bedroom C1',
    },
    beds: {
      label: 'Beds',
      type: 'text',
      defaultField: 'floorplans[2].floorplan_beds_min',
      defaultValue: '2',
    },
    baths: {
      label: 'Baths',
      type: 'text',
      defaultField: 'floorplans[2].floorplan_baths_min',
      defaultValue: '2',
    },
    sqft: {
      label: 'Sq Ft',
      type: 'text',
      defaultField: 'floorplans[2].floorplan_sqft_min',
      defaultValue: '1050',
    },
  },
  sourceBinding: {
    collection: 'floorplans',
    defaultIndex: 2,
    fieldMappings: {
      image: 'floorplan_image_url',
      alt: 'floorplan_name',
      name: 'floorplan_name',
      beds: 'floorplan_beds_min',
      baths: 'floorplan_baths_min',
      sqft: 'floorplan_sqft_min',
    },
  },
  generator: {
    prompt: 'Pick a distinct floor plan layout for this demo card — prefer a two-bedroom when available.',
    context: 'Card 3 of 4 in the manual floor plans grid. Different from cards 1, 2, and 4. Use the floor plan line drawing when present.',
  },
};

const plan4Def: SlotGroupDefinition = {
  label: 'Floor Plan 4',
  fields: {
    image: {
      label: 'Floor Plan Image',
      type: 'image',
      defaultField: 'floorplans[3].floorplan_image_url',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'floorplans[3].floorplan_name',
      defaultValue: 'Three bedroom floor plan',
    },
    name: {
      label: 'Plan Name',
      type: 'text',
      defaultField: 'floorplans[3].floorplan_name',
      defaultValue: 'Three Bedroom D1',
    },
    beds: {
      label: 'Beds',
      type: 'text',
      defaultField: 'floorplans[3].floorplan_beds_min',
      defaultValue: '3',
    },
    baths: {
      label: 'Baths',
      type: 'text',
      defaultField: 'floorplans[3].floorplan_baths_min',
      defaultValue: '2',
    },
    sqft: {
      label: 'Sq Ft',
      type: 'text',
      defaultField: 'floorplans[3].floorplan_sqft_min',
      defaultValue: '1280',
    },
  },
  sourceBinding: {
    collection: 'floorplans',
    defaultIndex: 3,
    fieldMappings: {
      image: 'floorplan_image_url',
      alt: 'floorplan_name',
      name: 'floorplan_name',
      beds: 'floorplan_beds_min',
      baths: 'floorplan_baths_min',
      sqft: 'floorplan_sqft_min',
    },
  },
  generator: {
    prompt: 'Pick a distinct floor plan layout for this demo card — prefer a larger three-bedroom when available.',
    context: 'Card 4 of 4 in the manual floor plans grid. Different from cards 1–3. Use the floor plan line drawing when present.',
  },
};

function asText(value: string | null | undefined, fallback: string): string {
  const raw = (value ?? '').trim();
  return raw || fallback;
}

function formatBeds(beds: string | null | undefined, fallback: string): string {
  const raw = asText(beds, fallback);
  const lower = raw.toLowerCase();
  if (lower === 'studio') return '0 BED';
  if (/bed/i.test(raw)) return raw.toUpperCase();
  const n = raw.replace(/[^\d.]/g, '');
  if (n && /^\d+(\.\d+)?$/.test(n)) return `${n} BED`;
  return `${raw} BED`;
}

function formatBaths(baths: string | null | undefined, fallback: string): string {
  const raw = asText(baths, fallback);
  const n = raw.replace(/[^\d.]/g, '') || raw;
  if (/^\d+(\.\d+)?$/.test(n)) return `${n} BATH`;
  if (/bath/i.test(raw)) return raw.toUpperCase();
  return `${raw} BATH`;
}

function formatSqft(sqft: string | null | undefined, fallback: string): string {
  const raw = asText(sqft, fallback);
  const cleaned = raw.replace(/,/g, '');
  if (/sq/i.test(raw)) return raw.replace(/sq\.?\s*ft\.?/i, 'Sq.ft.');
  if (/^\d+(\.\d+)?$/.test(cleaned)) {
    const num = Number(cleaned);
    return `${num.toLocaleString('en-US')} Sq.ft.`;
  }
  return `${raw} Sq.ft.`;
}

function parseBeds(beds: string | null | undefined, fallback: string): number {
  const raw = asText(beds, fallback).toLowerCase();
  if (!raw || raw === 'studio') return 0;
  const n = Number.parseFloat(raw.replace(/[^\d.]/g, ''));
  return Number.isFinite(n) ? n : 0;
}

function parseSqft(sqft: string | null | undefined, fallback: string): number {
  const raw = asText(sqft, fallback).replace(/,/g, '');
  const n = Number.parseFloat(raw.replace(/[^\d.]/g, ''));
  return Number.isFinite(n) ? n : 0;
}

type PlanId = 'plan_1' | 'plan_2' | 'plan_3' | 'plan_4';

interface PlanMeta {
  id: PlanId;
  plan: {
    image?: string | null;
    alt?: string | null;
    name?: string | null;
    beds?: string | null;
    baths?: string | null;
    sqft?: string | null;
  };
  fallbackName: string;
  fallbackBeds: string;
  fallbackBaths: string;
  fallbackSqft: string;
  fallbackAlt: string;
}

/** Display order: fewer beds first, then smaller sqft. */
function sortPlansByBedsThenSqft(plans: PlanMeta[]): PlanMeta[] {
  return [...plans].sort((a, b) => {
    const bedsDiff =
      parseBeds(a.plan.beds, a.fallbackBeds) - parseBeds(b.plan.beds, b.fallbackBeds);
    if (bedsDiff !== 0) return bedsDiff;
    const sqftDiff =
      parseSqft(a.plan.sqft, a.fallbackSqft) - parseSqft(b.plan.sqft, b.fallbackSqft);
    if (sqftDiff !== 0) return sqftDiff;
    return 0;
  });
}

interface PlanCardProps {
  plan: {
    image?: string | null;
    alt?: string | null;
    name?: string | null;
    beds?: string | null;
    baths?: string | null;
    sqft?: string | null;
  };
  fallbackName: string;
  fallbackBeds: string;
  fallbackBaths: string;
  fallbackSqft: string;
  cta: { url?: string | null; label?: string | null; _target?: string | null };
  children: ReactNode;
}

function PlanCard({
  plan,
  fallbackName,
  fallbackBeds,
  fallbackBaths,
  fallbackSqft,
  cta,
  children,
}: PlanCardProps) {
  const name = plan.name || fallbackName;

  return (
    <article className="mfp-card-inner">
      <header className="mfp-card-header">
        <h3 className="mfp-card-name font-heading">{name}</h3>
        <p className="mfp-card-specs font-body">
          {formatBeds(plan.beds, fallbackBeds)}
          <span className="mfp-sep" aria-hidden>
            {' '}
            |{' '}
          </span>
          {formatBaths(plan.baths, fallbackBaths)}
          <span className="mfp-sep" aria-hidden>
            {' '}
            |{' '}
          </span>
          {formatSqft(plan.sqft, fallbackSqft)}
        </p>
      </header>

      <div className="mfp-card-media">{children}</div>

      <a
        href={cta.url || '/contact'}
        target={cta._target || '_self'}
        className="mfp-btn font-heading"
      >
        {cta.label || 'Contact Us'}
      </a>
    </article>
  );
}

function PlanImage({
  plan,
  fallbackAlt,
  onOpen,
}: {
  plan: { image?: string | null; alt?: string | null; name?: string | null };
  fallbackAlt: string;
  onOpen?: () => void;
}) {
  const alt = plan.alt || plan.name || fallbackAlt;

  if (plan.image) {
    if (onOpen) {
      return (
        <button
          type="button"
          className="mfp-card-img-btn"
          onClick={onOpen}
          aria-label={`View larger ${alt}`}
        >
          <VibesitesImage
            url={plan.image}
            alt={alt}
            sizes="(min-width: 1100px) 30vw, (min-width: 700px) 45vw, 100vw"
            className="mfp-card-img"
          />
        </button>
      );
    }
    return (
      <VibesitesImage
        url={plan.image}
        alt={alt}
        sizes="(min-width: 1100px) 30vw, (min-width: 700px) 45vw, 100vw"
        className="mfp-card-img"
      />
    );
  }
  return (
    <div className="mfp-card-img-fallback" aria-hidden>
      <span className="mfp-card-img-fallback-label">Floor Plan</span>
    </div>
  );
}

function FloorplanLightbox({
  image,
  onClose,
}: {
  image: LightboxImage | null;
  onClose: () => void;
}) {
  useEffect(() => {
    if (!image) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', handleKey);
    };
  }, [image, onClose]);

  if (!image || typeof document === 'undefined') return null;

  return createPortal(
    <div
      className="mfp-lightbox"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Floor plan lightbox"
    >
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        className="mfp-lightbox__close"
        aria-label="Close lightbox"
      >
        &times;
      </button>

      <div
        className="mfp-lightbox__img-wrap"
        onClick={(e) => e.stopPropagation()}
      >
        <VibesitesImage
          url={image.image}
          alt={image.alt}
          sizes="90vw"
          className="mfp-lightbox__img"
        />
      </div>

      <style>{`
        .mfp-lightbox {
          position: fixed;
          inset: 0;
          z-index: 80;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(0, 0, 0, 0.85);
          padding: 1rem;
        }
        .mfp-lightbox__img-wrap {
          display: flex;
          align-items: center;
          justify-content: center;
          max-width: min(96vw, 1100px);
          max-height: 90vh;
        }
        .mfp-lightbox__img {
          max-width: 100%;
          max-height: 90vh;
          width: auto;
          height: auto;
          object-fit: contain;
          background: #fff;
          pointer-events: none;
          -webkit-user-drag: none;
          user-select: none;
        }
        .mfp-lightbox__close {
          position: absolute;
          top: 1rem;
          right: 1rem;
          color: #fff;
          font-size: 2.25rem;
          line-height: 1;
          background: none;
          border: none;
          cursor: pointer;
          opacity: 0.85;
          z-index: 10;
          padding: 0.25rem 0.5rem;
          transition: opacity 0.15s;
        }
        .mfp-lightbox__close:hover { opacity: 1; }
      `}</style>
    </div>,
    document.body,
  );
}

export function ManualFloorplans() {
  const disclaimer = useContentToken('manualfloorplans.disclaimer', {});
  const cta = useSlotGroup('manualfloorplans.cta', ctaDef);
  const plan1 = useSlotGroup('manualfloorplans.plan_1', plan1Def);
  const plan2 = useSlotGroup('manualfloorplans.plan_2', plan2Def);
  const plan3 = useSlotGroup('manualfloorplans.plan_3', plan3Def);
  const plan4 = useSlotGroup('manualfloorplans.plan_4', plan4Def);
  const [lightboxImage, setLightboxImage] = useState<LightboxImage | null>(null);

  const sortedPlans = sortPlansByBedsThenSqft([
    {
      id: 'plan_1',
      plan: plan1,
      fallbackName: 'Studio A1',
      fallbackBeds: '0',
      fallbackBaths: '1',
      fallbackSqft: '525',
      fallbackAlt: 'Studio floor plan',
    },
    {
      id: 'plan_2',
      plan: plan2,
      fallbackName: 'One Bedroom B1',
      fallbackBeds: '1',
      fallbackBaths: '1',
      fallbackSqft: '725',
      fallbackAlt: 'One bedroom floor plan',
    },
    {
      id: 'plan_3',
      plan: plan3,
      fallbackName: 'Two Bedroom C1',
      fallbackBeds: '2',
      fallbackBaths: '2',
      fallbackSqft: '1050',
      fallbackAlt: 'Two bedroom floor plan',
    },
    {
      id: 'plan_4',
      plan: plan4,
      fallbackName: 'Three Bedroom D1',
      fallbackBeds: '3',
      fallbackBaths: '2',
      fallbackSqft: '1280',
      fallbackAlt: 'Three bedroom floor plan',
    },
  ]);

  const openLightbox = (plan: {
    image?: string | null;
    alt?: string | null;
    name?: string | null;
  }, fallbackAlt: string) => {
    if (!plan.image) return;
    setLightboxImage({
      image: plan.image,
      alt: plan.alt || plan.name || fallbackAlt,
    });
  };

  return (
    <section id="manual-floorplans" className="mfp-section">
      <div className="mfp-inner">
        <div className="mfp-grid">
          {sortedPlans.map((card) => (
            <div key={card.id} className="mfp-card">
              <PlanCard
                plan={card.plan}
                fallbackName={card.fallbackName}
                fallbackBeds={card.fallbackBeds}
                fallbackBaths={card.fallbackBaths}
                fallbackSqft={card.fallbackSqft}
                cta={cta}
              >
                {card.id === 'plan_1' ? (
                  <EditableSlotGroup
                    groupPath="manualfloorplans.plan_1"
                    definition={plan1Def}
                    className="mfp-card-slot"
                  >
                    <PlanImage
                      plan={plan1}
                      fallbackAlt="Studio floor plan"
                      onOpen={() => openLightbox(plan1, 'Studio floor plan')}
                    />
                  </EditableSlotGroup>
                ) : null}
                {card.id === 'plan_2' ? (
                  <EditableSlotGroup
                    groupPath="manualfloorplans.plan_2"
                    definition={plan2Def}
                    className="mfp-card-slot"
                  >
                    <PlanImage
                      plan={plan2}
                      fallbackAlt="One bedroom floor plan"
                      onOpen={() => openLightbox(plan2, 'One bedroom floor plan')}
                    />
                  </EditableSlotGroup>
                ) : null}
                {card.id === 'plan_3' ? (
                  <EditableSlotGroup
                    groupPath="manualfloorplans.plan_3"
                    definition={plan3Def}
                    className="mfp-card-slot"
                  >
                    <PlanImage
                      plan={plan3}
                      fallbackAlt="Two bedroom floor plan"
                      onOpen={() => openLightbox(plan3, 'Two bedroom floor plan')}
                    />
                  </EditableSlotGroup>
                ) : null}
                {card.id === 'plan_4' ? (
                  <EditableSlotGroup
                    groupPath="manualfloorplans.plan_4"
                    definition={plan4Def}
                    className="mfp-card-slot"
                  >
                    <PlanImage
                      plan={plan4}
                      fallbackAlt="Three bedroom floor plan"
                      onOpen={() => openLightbox(plan4, 'Three bedroom floor plan')}
                    />
                  </EditableSlotGroup>
                ) : null}
              </PlanCard>
            </div>
          ))}
        </div>

        <EditableToken
          path="manualfloorplans.disclaimer"
          as="p"
          className="mfp-disclaimer font-body"
        >
          {disclaimer}
        </EditableToken>
      </div>

      <span className="hidden" aria-hidden style={{ display: 'none' }}>
        <EditableSlotGroup groupPath="manualfloorplans.cta" definition={ctaDef}>
          <span />
        </EditableSlotGroup>
      </span>

      <FloorplanLightbox
        image={lightboxImage}
        onClose={() => setLightboxImage(null)}
      />

      <style>{`
        .mfp-section {
          position: relative;
          overflow: hidden;
          background: #f3f3f3;
        }
        .mfp-inner {
          position: relative;
          z-index: 1;
          max-width: 1440px;
          margin: 0 auto;
          padding: clamp(48px, 6vw, 80px) clamp(20px, 3vw, 48px);
        }
        .mfp-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 20px;
          align-items: stretch;
        }
        .mfp-card {
          background: #fff;
          border: 1px solid #e4e4e4;
          min-width: 0;
          height: 100%;
        }
        .mfp-card-inner {
          display: flex;
          flex-direction: column;
          height: 100%;
          padding: 22px 22px 20px;
          gap: 16px;
        }
        .mfp-card-header {
          display: flex;
          flex-direction: column;
          gap: 6px;
          min-height: 3.4em;
        }
        .mfp-card-name {
          margin: 0;
          font-size: 15px;
          font-weight: 700;
          letter-spacing: 0.02em;
          text-transform: uppercase;
          color: #111;
          line-height: 1.3;
        }
        .mfp-card-specs {
          margin: 0;
          font-size: 12px;
          font-style: italic;
          font-weight: 400;
          line-height: 1.4;
          color: #5a5a5a;
        }
        .mfp-sep {
          font-style: normal;
          opacity: 0.7;
        }
        .mfp-card-media {
          flex: 1 1 auto;
          width: 100%;
          min-height: 220px;
          aspect-ratio: 1 / 1.05;
          border: 1px solid #e6e6e6;
          background: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
        }
        .mfp-card-slot {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .mfp-card-img-btn {
          display: block;
          width: 100%;
          height: 100%;
          padding: 0;
          margin: 0;
          border: none;
          background: transparent;
          cursor: zoom-in;
        }
        .mfp-card-img {
          width: 100%;
          height: 100%;
          object-fit: contain;
          object-position: center;
          padding: 10px;
          transition: opacity 0.2s ease;
        }
        .mfp-card-img-btn:hover .mfp-card-img {
          opacity: 0.85;
        }
        .mfp-card-img-fallback {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          background:
            linear-gradient(135deg,
              rgba(0, 0, 0, 0.04) 25%,
              transparent 25%),
            #f7f7f7;
          background-size: 16px 16px;
        }
        .mfp-card-img-fallback-label {
          font-family: var(--vibe-font-heading, sans-serif);
          font-size: 11px;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: #999;
        }
        .mfp-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          margin-top: auto;
          padding: 14px 16px;
          font-size: 12px;
          font-weight: 600;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          text-decoration: none;
          line-height: 1;
          background: var(--vibe-color-primary);
          color: var(--vibe-color-text-on-primary);
          border: 1.5px solid var(--vibe-color-primary);
          transition: background 0.2s ease, color 0.2s ease;
        }
        .mfp-btn:hover {
          background: #fff;
          color: var(--vibe-color-primary);
        }
        .mfp-disclaimer {
          font-size: 12px;
          font-style: italic;
          color: rgba(0, 0, 0, 0.45);
          text-align: center;
          margin: clamp(24px, 3vw, 40px) 0 0;
          line-height: 1.6;
        }

        @media (max-width: 1099px) {
          .mfp-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
        }
        @media (max-width: 639px) {
          .mfp-grid {
            grid-template-columns: 1fr;
            max-width: 420px;
            margin: 0 auto;
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ManualFloorplans',
  displayName: 'Manual Floor Plans',
  tokens: {
    'manualfloorplans.disclaimer': {
      label: 'Disclaimer',
      type: 'text',
      defaultValue:
        "Floor plans are an artist's interpretation. All dimensions are approximate. Please contact our leasing office for details.",
    },
  },
  slotGroups: {
    'manualfloorplans.cta': ctaDef,
    'manualfloorplans.plan_1': plan1Def,
    'manualfloorplans.plan_2': plan2Def,
    'manualfloorplans.plan_3': plan3Def,
    'manualfloorplans.plan_4': plan4Def,
  },
};

(ManualFloorplans as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default ManualFloorplans;
